import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useSessionStore from "../stores/useSessionStore";

const initialState = {
  name: "",
  email: "",
  company: "",
  website: "",
  phone: "",
  password: "",
  password_confirmation: "",
};

const SignUp = ({
  boxedLogo,
  boxedLogoLight,
  isLightTheme,
  accountType = "employer",
  signInPath = "/advertisers/sign-in",
  title = "Advertiser Sign-Up",
}) => {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [serverErrors, setServerErrors] = useState([]);
  const [success, setSuccess] = useState(false);

  // Use zustand store
  const handleSignUp = useSessionStore((state) => state.handleSignUp);
  const submitting = useSessionStore((state) => state.submitting);

  // Store redirect_to parameter in localStorage on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = urlParams.get("redirect_to");
    if (redirectTo) {
      localStorage.setItem("redirectAfterLogin", redirectTo);
    }
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(form.email))
      errs.email = "Invalid email address";
    if (!form.company.trim()) errs.company = "Company is required";
    // Website: only validate if not blank
    if (form.website.trim()) {
      if (
        !/^https?:\/\/[\w\-]+(\.[\w\-]+)+[/#?]?.*$/.test(form.website) &&
        !/^[\w\-]+(\.[\w\-]+)+[/#?]?.*$/.test(form.website)
      )
        errs.website = "Invalid website";
    }
    // Phone: only validate if not blank
    if (form.phone.trim()) {
      if (!/^[\d+\-\s()]{7,}$/.test(form.phone))
        errs.phone = "Invalid phone number";
    }
    if (!form.password) errs.password = "Password is required";
    else if (form.password.length < 6)
      errs.password = "Password must be at least 6 characters";
    if (!form.password_confirmation)
      errs.password_confirmation = "Confirm your password";
    else if (form.password !== form.password_confirmation)
      errs.password_confirmation = "Passwords do not match";
    return errs;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: undefined });
    setServerErrors([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerErrors([]);
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // Call zustand store's handleSignUp
    const {
      success: signupSuccess,
      errors: signupErrors,
      error,
    } = await handleSignUp({
      ...form,
      user_type: accountType,
    });

    if (signupSuccess) {
      setSuccess(true);
    } else {
      setServerErrors(
        signupErrors || (error ? [error] : ["Registration failed"])
      );
    }
  };

  return (
    <div className="sign-up-wrapper rainbow-section-gap">
      <div className="sign-up-box bg-flashlight">
        <div className="signup-box-top top-flashlight light-xl">
          <img
            className={`boxed-logo ${
              isLightTheme ? "boxed-logo-dark" : "boxed-logo-light"
            }`}
            src={isLightTheme ? boxedLogo : boxedLogoLight}
            width={180}
            alt="sign-up logo"
          />
        </div>
        <div className="separator-animated animated-true"></div>
        <div className="signup-box-bottom">
          {success ? (
            <div className="text-center">
              <div className="signup-alert-success">
                {`Registration successful! A confirmation email has been sent to ${form.email}. Please check your inbox and confirm your email before signing in.`}
              </div>
              <Link className="btn-default mt--10" to={signInPath}>
                Sign In
              </Link>
            </div>
          ) : (
            <>
              <div className="signup-box-content">
                <h4 className="title">{title}</h4>

                <form autoComplete="off" onSubmit={handleSubmit}>
                  <div className="input-section mail-section">
                    <div className="icon">
                      <i className="feather-user"></i>
                    </div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter Your Name"
                      autoComplete="off"
                      value={form.name}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.name && (
                    <div className="signup-alert signup-alert-danger">
                      {errors.name}
                    </div>
                  )}
                  <div className="input-section mail-section">
                    <div className="icon">
                      <i className="feather-mail"></i>
                    </div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter email address"
                      autoComplete="off"
                      value={form.email}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.email && (
                    <div className="signup-alert signup-alert-danger">
                      {errors.email}
                    </div>
                  )}
                  <div className="input-section mail-section">
                    <div className="icon">
                      <i className="feather-briefcase"></i>
                    </div>
                    <input
                      type="text"
                      name="company"
                      placeholder="Company"
                      autoComplete="off"
                      value={form.company}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.company && (
                    <div className="signup-alert signup-alert-danger">
                      {errors.company}
                    </div>
                  )}
                  <div className="input-section mail-section">
                    <div className="icon">
                      <i className="feather-globe"></i>
                    </div>
                    <input
                      type="text"
                      name="website"
                      placeholder="Website (Optional)"
                      autoComplete="off"
                      value={form.website}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.website && (
                    <div className="signup-alert signup-alert-danger">
                      {errors.website}
                    </div>
                  )}
                  <div className="input-section mail-section">
                    <div className="icon">
                      <i className="feather-phone"></i>
                    </div>
                    <input
                      type="text"
                      name="phone"
                      placeholder="Phone (Optional)"
                      autoComplete="off"
                      value={form.phone}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.phone && (
                    <div className="signup-alert signup-alert-danger">
                      {errors.phone}
                    </div>
                  )}
                  <div className="input-section password-section">
                    <div className="icon">
                      <i className="feather-lock"></i>
                    </div>
                    <input
                      type="password"
                      name="password"
                      placeholder="Create Password"
                      autoComplete="off"
                      value={form.password}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.password && (
                    <div className="signup-alert signup-alert-danger">
                      {errors.password}
                    </div>
                  )}
                  <div className="input-section password-section">
                    <div className="icon">
                      <i className="feather-lock"></i>
                    </div>
                    <input
                      type="password"
                      name="password_confirmation"
                      placeholder="Confirm Password"
                      autoComplete="off"
                      value={form.password_confirmation}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.password_confirmation && (
                    <div className="signup-alert signup-alert-danger">
                      {errors.password_confirmation}
                    </div>
                  )}
                  {serverErrors.length > 0 && (
                    <>
                      <p className="text-start mt--10 mb--10">
                        Server returned following errors:
                      </p>
                      <ul>
                        {serverErrors.map((err, i) => (
                          <li
                            className="signup-alert signup-alert-danger"
                            key={i}
                          >
                            {err}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  <button
                    type="submit"
                    className="btn-default"
                    disabled={submitting}
                  >
                    {submitting ? "Signing Up..." : "Sign Up"}
                  </button>
                </form>
              </div>
              <div className="signup-box-footer">
                <div className="bottom-text">
                  Don&apos;t have an account?
                  <Link className="btn-read-more ps-2" to={signInPath}>
                    <span>Sign In</span>
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignUp;
